import { http, download } from './request'
import { apiConfig } from './config'
import {
  asJobStart,
  asJobStatus,
  downloadExportJobExcel,
  type OverseasExportJobStart,
  type OverseasExportJobStatus,
} from './overseasSensitiveExport'
import {
  OVERSEAS_IMPORT_HTTP_TIMEOUT_MS,
  OVERSEAS_IMPORT_PREVIEW_HTTP_TIMEOUT_MS,
  OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS,
  parseImportChunkResult,
  parseImportPreviewResult as parsePreviewChunkResult,
} from '@/types/importPreview'
import { toPlainOrCipherPlaceholder } from '@/utils/confidentialityDisplay'

export type ForeignPersonnelGender = 'male' | 'female' | 'unknown'

/** 表单草稿：未选性别时为空串 */
export type ForeignPersonnelFormGender = ForeignPersonnelGender | ''

/** 是否有回国计划：与库表一致可用 1/0 */
export type PersonnelHasReturnPlan = '1' | '0'

/** 是否帮他人代办：与库表一致 1/0 */
export type PersonnelProxyForOthers = '1' | '0'

/** 列表接口 /biz/overseas/personnel/page 人员归国状态 */
export type OverseasPersonnelListStatus = 'OVERSEAS' | 'RETURNED'

/**
 * 分页列表行（与新增/编辑表单字段对应；接口字段别名在 rawToOverseasListItem 中归一）
 */
export interface OverseasPersonnelListItem {
  id: string
  /** 身份证号码 */
  idCard: string
  name: string
  /** 出生日期 YYYY-MM-DD */
  birthDate: string
  passportNo: string
  /** 后端可能为 1/2、male/female 等 */
  gender: string | number
  /** 护照有效期，与表单 passportExpiry 同源 */
  passportValidity: string
  /** 手机号 */
  phone: string
  /** 列表展示：优先国家中文名，否则国家编码 */
  overseasCountry: string
  /** 前往国家编码（库 overseas_country） */
  overseasCountryCode?: string
  /** 前往国家中文名（库 overseas_country_name） */
  overseasCountryName?: string
  /** 身份证件住址（身份证人像面地址，id_card_address） */
  idCardAddress?: string
  /** 住址（接口 domesticAddress） */
  domesticAddress: string
  /** 境外地址 */
  overseasAddress: string
  /** 国内户籍地（接口 domesticProvince） */
  domesticProvince: string
  /** 国内户籍区县区划码 */
  domesticAddressCode?: string
  emergencyContact: string
  emergencyPhone: string
  nation?: string
  travelPurpose?: string
  /** 人员类别（字典 personnel_category 的 dictValue） */
  personnelCategory?: string
  hasReturnPlan?: string
  /** 是否帮他人代办 1/0 */
  proxyForOthers?: string
  expectedReturnDate?: string
  remark?: string
  status: OverseasPersonnelListStatus | ''
  issueAuthority?: string
  idCardValidity?: string
  visaValidity?: string
  enterpriseId?: string
  enterpriseName?: string
  overseasPhone?: string
  overseasLongitude?: number | null
  overseasLatitude?: number | null
}

export interface ForeignPersonnelRecord {
  id: string
  name: string
  idCardNo: string
  /** 出生日期 YYYY-MM-DD */
  birthDate: string
  gender: ForeignPersonnelGender
  /** 民族（字典 dictValue） */
  nation: string
  passportNo: string
  /** 护照有效期 YYYY-MM-DD */
  passportExpiry: string
  /** 手机号 */
  contactPhone: string
  /** 前往国家编码（与国别 countryCode 一致） */
  overseasCountryCode: string
  /** 前往国家中文名称 */
  overseasCountryName: string
  /** 所在国家地区最后一级ID（geonameid） */
  overseasGeonameid?: string
  /** 所在国家地区最后一级名称 */
  overseasGeonameName?: string
  /** 身份证件住址 */
  idCardAddress?: string
  /** 住址（对应接口 domesticAddress） */
  domesticAddress: string
  /** 境外地址 */
  overseasAddress: string
  overseasLongitude?: number | null
  overseasLatitude?: number | null
  /** 国内户籍地（公共地区级联路径，如 贵州省 / 贵阳市 / 南明区） */
  householdProvince: string
  /** 国内户籍区县区划码（末级区县 code，与 domestic_address_code 对应） */
  domesticAddressCode?: string
  emergencyContactName: string
  emergencyContactPhone: string
  /** 前往目的（字典 dictValue） */
  travelPurpose: string
  /** 人员类别（字典 personnel_category 的 dictValue，与前往目的分开） */
  personnelCategory: string
  /** 是否有回国计划 1/0 */
  hasReturnPlan: PersonnelHasReturnPlan
  /** 是否帮他人代办 1/0 */
  proxyForOthers: PersonnelProxyForOthers
  /** 预计归国日期 YYYY-MM-DD */
  expectedReturnDate: string
  /** OVERSEAS / RETURNED */
  personnelStatus: OverseasPersonnelListStatus
  remark: string
  /** 身份证签发机关（OCR/详情） */
  issueAuthority?: string
  /** 身份证有效期限文案（OCR/详情） */
  idCardValidity?: string
  /** 证件照 MinIO 文件 ID */
  idCardFrontPhoto?: string
  idCardBackPhoto?: string
  passportPhoto?: string
  visaPhoto?: string
  departurePhoto?: string
  entryPhoto?: string
  /** 签证/签注有效期限（YYYY-MM-DD 或库表存文案） */
  visaValidity?: string
  /** 国内工作单位：biz_enterprise.id */
  enterpriseId?: string
  /** 国内工作单位名称（与 enterpriseId 可同时存在；仅手写时不传 enterpriseId） */
  enterpriseName?: string
  /** 境外联系电话 */
  overseasPhone?: string
}

/** GET /biz/overseas/personnel/page 查询参数 */
export interface ForeignPersonnelPageParams {
  current: number
  size: number
  /** 姓名 / 身份证 */
  searchKey?: string
  /** 国内户籍区划码查询前缀（按省/市/区县截断为 2/4/6 位，与库 9 位 domestic_address_code 前缀匹配） */
  areaCode?: string
  /** 定位状态：OVERSEAS-国外 / RETURNED-国内 */
  status?: 'OVERSEAS' | 'RETURNED'
  /** 所属企业（biz_enterprise.id） */
  enterpriseId?: string
  /** 前往国家编码（Personnel.overseasCountry） */
  overseasCountry?: string
}

export interface ForeignPersonnelPageResult {
  list: OverseasPersonnelListItem[]
  total: number
}

/** 列表 gender → 表单 ForeignPersonnelGender */
export function listGenderToRecordGender(gender: unknown): ForeignPersonnelGender {
  const g = gender
  if (g === 2 || g === '2' || g === 'female' || g === 'FEMALE' || g === '女') return 'female'
  if (g === 3 || g === '3' || g === 'unknown' || g === 'UNKNOWN' || g === '未知') return 'unknown'
  return 'male'
}

/** 列表性别展示（与表单「男/女/未知」一致） */
export function formatPersonnelGenderLabel(gender: unknown): string {
  if (
    gender === 2 ||
    gender === '2' ||
    gender === 'female' ||
    gender === 'FEMALE' ||
    gender === '女'
  )
    return '女'
  if (
    gender === 3 ||
    gender === '3' ||
    gender === 'unknown' ||
    gender === 'UNKNOWN' ||
    gender === '未知'
  )
    return '未知'
  if (gender === 1 || gender === '1' || gender === 'male' || gender === 'MALE' || gender === '男')
    return '男'
  const s = String(gender ?? '').trim()
  return s || '—'
}

/** 将列表行映射为表单用的结构（与 OverseasPersonnelListItem 字段关联） */
export function overseasListItemToRecord(item: OverseasPersonnelListItem): ForeignPersonnelRecord {
  return {
    id: item.id,
    name: item.name,
    idCardNo: item.idCard,
    birthDate: item.birthDate,
    gender: listGenderToRecordGender(item.gender),
    nation: item.nation ?? '',
    passportNo: item.passportNo,
    passportExpiry: item.passportValidity,
    contactPhone: item.phone,
    overseasCountryCode: item.overseasCountryCode ?? '',
    overseasCountryName: item.overseasCountryName ?? '',
    idCardAddress: item.idCardAddress ?? '',
    domesticAddress: item.domesticAddress,
    overseasAddress: item.overseasAddress ?? '',
    overseasLongitude: item.overseasLongitude ?? null,
    overseasLatitude: item.overseasLatitude ?? null,
    householdProvince: item.domesticProvince,
    domesticAddressCode: item.domesticAddressCode,
    emergencyContactName: item.emergencyContact,
    emergencyContactPhone: item.emergencyPhone,
    travelPurpose: item.travelPurpose ?? '',
    personnelCategory: item.personnelCategory ?? '',
    hasReturnPlan: normalizeHasReturnPlanCode(item.hasReturnPlan),
    proxyForOthers: normalizeProxyForOthersCode(item.proxyForOthers),
    expectedReturnDate: (item.expectedReturnDate ?? '').slice(0, 10),
    personnelStatus: ((): OverseasPersonnelListStatus => {
      const s = String(item.status ?? '').toUpperCase()
      return s === 'RETURNED' ? 'RETURNED' : 'OVERSEAS'
    })(),
    remark: item.remark ?? '',
    issueAuthority: item.issueAuthority ?? '',
    idCardValidity: item.idCardValidity ?? '',
    idCardFrontPhoto: '',
    idCardBackPhoto: '',
    passportPhoto: '',
    visaPhoto: '',
    departurePhoto: '',
    entryPhoto: '',
    visaValidity: item.visaValidity ?? '',
    enterpriseId: item.enterpriseId ?? '',
    enterpriseName: item.enterpriseName ?? '',
    overseasPhone: item.overseasPhone ?? '',
  }
}

function normalizeHasReturnPlanCode(v: unknown): PersonnelHasReturnPlan {
  if (v === true || v === 'true' || v === 1 || v === '1' || v === '是') return '1'
  return '0'
}

function normalizeProxyForOthersCode(v: unknown): PersonnelProxyForOthers {
  if (v === true || v === 'true' || v === 1 || v === '1' || v === '是') return '1'
  return '0'
}

/** 详情接口 → 编辑表单模型 */
export function overseasPersonnelDetailToRecord(
  d: OverseasPersonnelDetail,
): ForeignPersonnelRecord | null {
  const id = d.id?.trim()
  if (!id) return null
  return {
    id,
    name: String(d.name ?? ''),
    idCardNo: String(d.idCard ?? ''),
    birthDate: String(d.birthDate ?? '').slice(0, 10),
    gender: listGenderToRecordGender(d.gender),
    nation: String(d.nation ?? ''),
    passportNo: String(d.passportNo ?? ''),
    passportExpiry: String(d.passportValidity ?? '').slice(0, 10),
    contactPhone: String(d.phone ?? ''),
    overseasCountryCode: String(d.overseasCountry ?? '').trim(),
    overseasCountryName: String(d.overseasCountryName ?? '').trim(),
    overseasGeonameName: String(d.overseasGeonameName ?? '').trim(),
    idCardAddress: String(d.idCardAddress ?? ''),
    domesticAddress: String(d.domesticAddress ?? ''),
    overseasAddress: String(d.overseasAddress ?? ''),
    overseasLongitude: d.overseasLongitude ?? null,
    overseasLatitude: d.overseasLatitude ?? null,
    householdProvince: String(d.domesticProvince ?? ''),
    domesticAddressCode: d.domesticAddressCode ? String(d.domesticAddressCode) : '',
    emergencyContactName: String(d.emergencyContact ?? ''),
    emergencyContactPhone: String(d.emergencyPhone ?? ''),
    travelPurpose: String(d.travelPurpose ?? ''),
    personnelCategory: String(d.personnelCategory ?? ''),
    hasReturnPlan: normalizeHasReturnPlanCode(d.hasReturnPlan),
    proxyForOthers: normalizeProxyForOthersCode(d.proxyForOthers),
    expectedReturnDate: String(d.expectedReturnDate ?? '').slice(0, 10),
    personnelStatus: ((): OverseasPersonnelListStatus => {
      const s = String(d.status ?? '').toUpperCase()
      return s === 'RETURNED' ? 'RETURNED' : 'OVERSEAS'
    })(),
    remark: String(d.remark ?? ''),
    issueAuthority: String(d.issueAuthority ?? ''),
    idCardValidity: normalizeIdCardValidityToStandardFormat(String(d.idCardValidity ?? '')),
    idCardFrontPhoto: String(d.idCardFrontPhoto ?? ''),
    idCardBackPhoto: String(d.idCardBackPhoto ?? ''),
    passportPhoto: String(d.passportPhoto ?? ''),
    visaPhoto: String(d.visaPhoto ?? ''),
    departurePhoto: String(d.departurePhoto ?? ''),
    entryPhoto: String(d.entryPhoto ?? ''),
    visaValidity: String(d.visaValidity ?? ''),
    enterpriseId: d.enterpriseId != null && d.enterpriseId !== '' ? String(d.enterpriseId) : '',
    enterpriseName: String(d.enterpriseName ?? '').trim(),
    overseasPhone: String(d.overseasPhone ?? ''),
  }
}

export type ForeignPersonnelSavePayload = Omit<ForeignPersonnelRecord, 'id' | 'gender'> & {
  gender: ForeignPersonnelFormGender
}

/**
 * GET /biz/overseas/personnel/detail?id= 人员详情（与后台人员档案字段一致）
 */
export interface OverseasPersonnelDetail {
  id?: string
  name?: string
  idCard?: string
  issueAuthority?: string
  idCardValidity?: string
  /** 身份证件住址 */
  idCardAddress?: string
  /** 1男 2女 3未知 */
  gender?: number | string
  birthDate?: string
  nation?: string
  passportNo?: string
  passportValidity?: string
  visaValidity?: string
  domesticGeonameid?: string | number
  domesticProvince?: string
  domesticAddressCode?: string
  domesticAddress?: string
  phone?: string
  emergencyContact?: string
  emergencyPhone?: string
  enterpriseId?: string | number
  enterpriseName?: string
  overseasGeonameid?: string | number
  /** 境外地区中文名（与 overseasGeonameid 对应） */
  overseasGeonameName?: string
  overseasCountry?: string
  overseasCountryName?: string
  overseasAddress?: string
  overseasPhone?: string
  overseasLongitude?: number | null
  overseasLatitude?: number | null
  travelPurpose?: string
  /** 人员类别（字典 personnel_category） */
  personnelCategory?: string
  /** 定位状态：OVERSEAS-国外 / RETURNED-国内 */
  status?: string
  /** 完整性：PASSED 完整 / TAMPERED 已篡改 / UNSIGNED 未签名 / DISABLED 未启用 */
  addressIntegrityStatus?: string
  /** 地址完整性中文：完整 / 已篡改 / 未签名 / 未启用 */
  addressIntegrityStatusName?: string
  idCardFrontPhoto?: string
  idCardBackPhoto?: string
  passportPhoto?: string
  visaPhoto?: string
  departurePhoto?: string
  entryPhoto?: string
  hasReturnPlan?: boolean | string | number
  proxyForOthers?: boolean | string | number
  expectedReturnDate?: string
  remark?: string
  deleteFlag?: string
  createUser?: string
  /** 创建人账号（后端详情填充） */
  createUserName?: string
  /** 关联账号（系统用户 ID） */
  linkedUserId?: string
  /** 关联账号中文姓名（后端详情填充） */
  linkedUserName?: string
  /** 关联账号类型：100 政府 / 200 个人 / 300 企业 */
  linkedUserMode?: number
  createTime?: string
  updateUser?: string
  /** 更新人账号（后端详情填充） */
  updateUserName?: string
  updateTime?: string
}

/** 详情页：空为空白（不显示 —） */
export function detailPlain(v: unknown): string {
  if (v === null || v === undefined) return ''
  return String(v).trim()
}

/** 人员境外「所在国家」展示：优先中文名，否则编码（兼容旧数据仅一列） */
export function personnelOverseasCountryDisplay(d: {
  overseasCountry?: string | null
  overseasCountryName?: string | null
}): string {
  const name = String(d.overseasCountryName ?? '').trim()
  const code = String(d.overseasCountry ?? '').trim()
  return name || code
}

/** 详情-性别：1男 2女 3未知 */
export function formatDetailGender(g: unknown): string {
  const n = Number(g)
  if (n === 1) return '男'
  if (n === 2) return '女'
  if (n === 3) return '未知'
  if (g === '男' || g === '女' || g === '未知') return String(g)
  return detailPlain(g)
}

/** 详情-地址完整性状态 */
export function formatDetailAddressIntegrity(status: unknown, statusName?: unknown): string {
  const name = String(statusName ?? '').trim()
  if (name) return name
  const u = String(status ?? '').toUpperCase()
  if (u === 'PASSED') return '完整'
  if (u === 'TAMPERED') return '已篡改'
  if (u === 'UNSIGNED') return '未签名'
  if (u === 'DISABLED') return '未启用'
  return detailPlain(status)
}

/** 详情-人员状态 OVERSEAS-未归国 / RETURNED-已归国 */
export function formatDetailOverseasStatus(s: unknown): string {
  const u = String(s ?? '').toUpperCase()
  if (u === 'OVERSEAS') return '未归国'
  if (u === 'RETURNED') return '已归国'
  return detailPlain(s)
}

/** 是否有回国计划 */
export function formatDetailYesNo(v: unknown): string {
  if (v === true || v === 'true' || v === 1 || v === '1' || v === '是') return '是'
  if (v === false || v === 'false' || v === 0 || v === '0' || v === '否') return '否'
  return detailPlain(v)
}

/** 通用展示：空为 — */
export function detailText(v: unknown): string {
  if (v === null || v === undefined) return '—'
  const s = String(v).trim()
  return s || '—'
}

/**
 * 云存储文件 ID → 可给 &lt;img src&gt; 使用的地址（同步拼接，与 PermissionApplicationDetailModal 下载路径一致）。
 * 需要缩略图或 MinIO 直链时可用 api/overseasCommonFile.resolveCommonFileImageSrc（走 detailById）。
 */
export function personnelFilePreviewUrl(fileId: string | undefined | null): string | null {
  const id = fileId?.trim()
  if (!id) return null
  if (/^https?:\/\//i.test(id)) return id
  if (id.startsWith('data:')) return id
  const base = (apiConfig.baseURL || '').replace(/\/$/, '')
  const path = `/dev/file/download?id=${encodeURIComponent(id)}`
  if (!base) return path
  return `${base}${path}`
}

/** 下拉选项「【企业】xxx」提交时去掉前缀，存库为纯单位名称 */
function normalizeDomesticEnterpriseNameForSave(raw: string | undefined | null): string {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  const m = s.match(/^【(?:企业|项目)】\s*(.+)$/)
  return m?.[1]?.trim() ?? s
}

/**
 * POST /biz/overseas/personnel/save 请求体（与后台字段一致）
 * id：不传或空为新增，有值为更新
 */
export interface OverseasPersonnelSaveBody {
  id?: string
  idCard?: string
  name?: string
  birthDate?: string
  gender?: 1 | 2 | 3
  nation: string
  passportNo: string
  passportValidity: string
  phone: string
  /** 国家两位编码 */
  overseasCountry: string
  /** 国家中文名称 */
  overseasCountryName?: string
  /** 所在国家地区最后一级ID（geonameid） */
  overseasGeonameid?: string
  /** 所在国家地区最后一级名称 */
  overseasGeonameName?: string
  overseasAddress: string
  overseasLongitude?: number | null
  overseasLatitude?: number | null
  domesticAddress: string
  domesticProvince: string
  domesticAddressCode: string
  emergencyContact: string
  emergencyPhone: string
  travelPurpose: string
  /** 人员类别；编辑时传空字符串清空 */
  personnelCategory: string
  hasReturnPlan: string
  proxyForOthers: string
  /** 有值时才传，避免空字符串反序列化 Date 失败 */
  expectedReturnDate?: string
  /** 定位状态：OVERSEAS-国外 / RETURNED-国内 */
  status: string
  remark: string
  issueAuthority?: string
  idCardValidity?: string
  idCardAddress?: string
  idCardFrontPhoto?: string
  idCardBackPhoto?: string
  passportPhoto?: string
  visaPhoto?: string
  departurePhoto?: string
  entryPhoto?: string
  visaValidity?: string
  enterpriseId?: string
  enterpriseName?: string
  overseasPhone?: string
}

/** 表单模型 → 保存接口入参（新增与编辑均提交完整业务字段） */
export function toOverseasPersonnelSaveBody(
  payload: ForeignPersonnelSavePayload,
  editId?: string | null,
): OverseasPersonnelSaveBody {
  const id = editId?.trim()
  const body: OverseasPersonnelSaveBody = {
    passportNo: payload.passportNo.trim(),
    passportValidity: normalizePassportValidityToIso(payload.passportExpiry),
    phone: payload.contactPhone.trim(),
    overseasCountry: payload.overseasCountryCode?.trim() ?? '',
    overseasCountryName: payload.overseasCountryName?.trim() ?? '',
    overseasGeonameid: payload.overseasGeonameid?.trim(),
    overseasGeonameName: payload.overseasGeonameName?.trim(),
    overseasAddress: payload.overseasAddress?.trim() ?? '',
    overseasLongitude: payload.overseasLongitude ?? null,
    overseasLatitude: payload.overseasLatitude ?? null,
    domesticAddress: payload.domesticAddress.trim(),
    idCardAddress: (payload.idCardAddress ?? '').trim(),
    domesticProvince: payload.householdProvince.trim(),
    domesticAddressCode: (payload.domesticAddressCode ?? '').trim(),
    emergencyContact: payload.emergencyContactName.trim(),
    emergencyPhone: payload.emergencyContactPhone.trim(),
    nation: payload.nation.trim(),
    travelPurpose: payload.travelPurpose?.trim() ?? '',
    personnelCategory: payload.personnelCategory?.trim() ?? '',
    hasReturnPlan: payload.hasReturnPlan,
    proxyForOthers: id ? (payload.proxyForOthers === '1' ? '1' : '0') : '1',
    status: payload.personnelStatus?.trim() || 'OVERSEAS',
    remark: payload.remark?.trim() ?? '',
  }
  const ia = payload.issueAuthority?.trim()
  if (ia) body.issueAuthority = ia
  const icv = payload.idCardValidity?.trim()
  if (icv) body.idCardValidity = normalizeIdCardValidityToStandardFormat(icv)
  const icf = payload.idCardFrontPhoto?.trim()
  if (icf) body.idCardFrontPhoto = icf
  const icb = payload.idCardBackPhoto?.trim()
  if (icb) body.idCardBackPhoto = icb
  const pp = payload.passportPhoto?.trim()
  if (pp) body.passportPhoto = pp
  body.visaPhoto = payload.visaPhoto?.trim() ?? ''
  body.departurePhoto = payload.departurePhoto?.trim() ?? ''
  body.entryPhoto = payload.entryPhoto?.trim() ?? ''
  const vv = payload.visaValidity?.trim()
  if (vv) body.visaValidity = vv
  const ent = payload.enterpriseId?.trim()
  if (ent) body.enterpriseId = ent
  body.enterpriseName = normalizeDomesticEnterpriseNameForSave(payload.enterpriseName)
  const op = payload.overseasPhone?.trim()
  if (op) body.overseasPhone = op
  const er = payload.expectedReturnDate?.trim()
  if (er) body.expectedReturnDate = er
  if (id) {
    body.id = id
  }
  body.idCard = payload.idCardNo.trim()
  body.name = payload.name.trim()
  body.birthDate = payload.birthDate
  const g = payload.gender
  if (g === 'female') body.gender = 2
  else if (g === 'unknown') body.gender = 3
  else if (g === 'male') body.gender = 1
  else throw new Error('请选择性别')
  return body
}

/**
 * 新增 / 编辑人员（同一接口）
 * @param editId 编辑时传人员 id，新增不传或传 null
 */
export async function saveOverseasPersonnel(
  payload: ForeignPersonnelSavePayload,
  editId?: string | null,
): Promise<void> {
  const body = toOverseasPersonnelSaveBody(payload, editId)
  await http.post<void>('/biz/overseas/personnel/save', body)
}

/**
 * 省内地区展示：从户籍地「省 / 市 / 区」中去掉省级，仅展示市、区（多段用「 / 」连接）
 */
export function formatInnerProvinceRegion(householdProvince: string): string {
  const parts = householdProvince
    .split(/\s*\/\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length <= 1) return householdProvince.trim() || '—'
  return parts.slice(1).join(' / ')
}

/** 身份证号：明文原样，密文 ****** */
export function maskIdCard(idCard: string): string {
  return toPlainOrCipherPlaceholder(idCard)
}

/** 手机号：明文原样，密文 ****** */
export function maskPhone(phone: string): string {
  return toPlainOrCipherPlaceholder(phone)
}

/** 中国大陆 18 位身份证校验（末位可为 X） */
export function isValidChineseIdCard(value: string): boolean {
  const s = value.trim().toUpperCase()
  if (!/^\d{17}[\dX]$/.test(s)) return false
  const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2]
  const codes = '10X98765432'
  let sum = 0
  for (let i = 0; i < 17; i++) sum += Number(s[i]) * weights[i]!
  return codes[sum % 11] === s[17]
}

/**
 * 从 18 位身份证号码解析出生日期（第 7–14 位为 YYYYMMDD）
 * @returns `YYYY-MM-DD`，无法解析时返回 `null`
 */
export function parseBirthDateFromChineseIdCard(value: string): string | null {
  const s = value.trim().toUpperCase()
  if (!/^\d{17}[\dX]$/.test(s)) return null
  const ymd = s.slice(6, 14)
  const y = Number(ymd.slice(0, 4))
  const m = Number(ymd.slice(4, 6))
  const d = Number(ymd.slice(6, 8))
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  if (y < 1900 || y > 2100) return null
  const dt = new Date(y, m - 1, d)
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** 从 18 位身份证第 17 位解析性别：奇数为男，偶数为女 */
export function parseGenderFromChineseIdCard(value: string): ForeignPersonnelGender | null {
  const s = value.trim().toUpperCase()
  if (!/^\d{17}[\dX]$/.test(s)) return null
  const n = Number(s[16])
  if (!Number.isFinite(n)) return null
  return n % 2 === 1 ? 'male' : 'female'
}

/** OCR 常见 8 位数字日期 YYYYMMDD → YYYY-MM-DD */
export function parseOcrCompactYmdToIso(value: unknown): string {
  const s = String(value ?? '').replace(/\D/g, '')
  if (s.length !== 8) return ''
  const y = Number(s.slice(0, 4))
  const m = Number(s.slice(4, 6))
  const d = Number(s.slice(6, 8))
  if (!Number.isFinite(y) || m < 1 || m > 12 || d < 1 || d > 31) return ''
  if (y < 1900 || y > 2199) return ''
  const dt = new Date(y, m - 1, d)
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return ''
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

/** 护照有效期：OCR 纯数字 YYYYMMDD 与表单 yyyy-MM-dd 统一为 ISO 日期字符串 */
export function normalizePassportValidityToIso(input: unknown): string {
  const s = String(input ?? '').trim()
  if (!s) return ''
  const digits = s.replace(/\D/g, '')
  if (digits.length === 8) {
    const iso = parseOcrCompactYmdToIso(digits)
    if (iso) return iso
  }
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s)
  if (m) {
    const y = m[1]
    const mo = String(Number(m[2])).padStart(2, '0')
    const d = String(Number(m[3])).padStart(2, '0')
    return `${y}-${mo}-${d}`
  }
  return s
}

/** 身份证/护照 OCR 出生日期：支持 YYYYMMDD 或「1990年1月1日」 */
export function normalizeOcrBirthFlexible(value: unknown): string {
  const s = String(value ?? '').trim()
  if (!s) return ''
  const iso = parseOcrCompactYmdToIso(s)
  if (iso) return iso
  const m = s.match(/(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?/)
  if (m) {
    const y = Number(m[1])
    const mo = Number(m[2])
    const d = Number(m[3])
    if (y >= 1900 && y <= 2100 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }
  return ''
}

/** OCR 性别文案（含「男/M」）→ 表单 gender */
export function ocrGenderTextToRecord(g: string | undefined): ForeignPersonnelGender | null {
  const t = String(g ?? '').trim()
  if (!t) return null
  const head = (t.split(/[/／|]/)[0] ?? '').trim()
  if (/^女|^F$/i.test(head) || head === '女') return 'female'
  if (/未知|unknown/i.test(head)) return 'unknown'
  if (/^男|^M$/i.test(head) || head === '男') return 'male'
  if (/女|F/i.test(t) && !/男/.test(t)) return 'female'
  if (/男|M/i.test(t)) return 'male'
  return null
}

/** 民族 OCR（如「汉」）匹配字典项 value */
export function resolveNationDictValue(
  ethnicity: string | undefined,
  options: { label: string; value: string }[],
): string | null {
  const e = String(ethnicity ?? '').trim()
  if (!e) return null
  for (const o of options) {
    if (o.label === e || o.value === e) return o.value
    if (o.label.includes(e) || e.includes(o.label)) return o.value
  }
  if (e === '汉') {
    const han = options.find((o) => /汉/.test(o.label))
    if (han) return han.value
  }
  return null
}

/** 身份证反面签发日、失效日 → 有效期限展示文案 */
export function idCardValidityFromOcrIssueExpiry(issue: unknown, expiry: unknown): string {
  const a = parseOcrCompactYmdToIso(issue)
  const b = parseOcrCompactYmdToIso(expiry)
  if (a && b) return `${a} 至 ${b}`
  return b || a || ''
}

/**
 * 解析库表/表单中身份证有效期限文案为起止 ISO 日期（供 RangePicker）
 * 支持：`YYYY-MM-DD 至 YYYY-MM-DD`、`-`/`～` 分隔、`YYYY.MM.DD`、`YYYYMMDD` 连续两段等
 */
export function parseIdCardValidityRangeIso(value: unknown): { start: string; end: string } | null {
  const t = String(value ?? '').trim()
  if (!t) return null
  const join16 = t.replace(/\D/g, '')
  if (join16.length === 16) {
    const a = parseOcrCompactYmdToIso(join16.slice(0, 8))
    const b = parseOcrCompactYmdToIso(join16.slice(8, 16))
    if (a && b) return { start: a, end: b }
  }
  const mZhi = t.match(/^(\d{4}-\d{2}-\d{2})\s*至\s*(\d{4}-\d{2}-\d{2})$/)
  if (mZhi?.[1] && mZhi[2]) return { start: mZhi[1], end: mZhi[2] }
  const normalized = t.replace(/\./g, '-').replace(/年|月/g, '-').replace(/日/g, '')
  const parts = normalized
    .split(/\s*(?:至|到|～|~|–|-)\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length >= 2) {
    const p0 = parts[0]!
    const p1 = parts[1]!
    const a = parseOcrCompactYmdToIso(p0.replace(/\D/g, '')) || strictYmd(p0)
    const b = parseOcrCompactYmdToIso(p1.replace(/\D/g, '')) || strictYmd(p1)
    if (a && b) return { start: a, end: b }
  }
  const compact = t.match(/^(\d{8})\s*[-至到～]?\s*(\d{8})$/)
  if (compact?.[1] && compact[2]) {
    const a = parseOcrCompactYmdToIso(compact[1])
    const b = parseOcrCompactYmdToIso(compact[2])
    if (a && b) return { start: a, end: b }
  }
  return null
}

/** 身份证有效期限：区间 `yyyy-MM-dd 至 yyyy-MM-dd` 或单日 `yyyy-MM-dd`；「长期」保留 */
export function normalizeIdCardValidityToStandardFormat(value: unknown): string {
  const t = String(value ?? '').trim()
  if (!t) return ''
  if (/长期|长期有效/.test(t)) return t
  const r = parseIdCardValidityRangeIso(t)
  if (r?.start && r?.end) return `${r.start} 至 ${r.end}`
  const one = normalizePassportValidityToIso(t)
  if (one && /^\d{4}-\d{2}-\d{2}$/.test(one)) return one
  return t
}

function strictYmd(s: string): string | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const ys = m?.[1]
  const ms = m?.[2]
  const ds = m?.[3]
  if (!ys || !ms || !ds) return null
  const y = Number(ys)
  const mo = Number(ms)
  const d = Number(ds)
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null
  const dt = new Date(y, mo - 1, d)
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null
  return `${ys}-${ms}-${ds}`
}

const PERSONNEL_OCR_BASE = '/biz/overseas/personnel'

/** 身份证正面 OCR 结果（与后台 idCardFront 节点一致） */
export interface PersonnelOcrIdCardFront {
  address?: string
  riskType?: string
  ethnicity?: string
  gender?: string
  name?: string
  birth?: string
  imageStatus?: string
  idNumber?: string
  /** MinIO 上传返回的可访问地址（与 /dev/file/uploadMinioReturnUrl 同源） */
  url?: string
  /** 历史：dev_file 主键 */
  fileId?: string
}

export interface PersonnelOcrIdCardBack {
  expiryDate?: string
  issuingAuthority?: string
  riskType?: string
  issueDate?: string
  imageStatus?: string
  url?: string
  fileId?: string
}

export interface PersonnelOcrPassport {
  passportNumber?: string
  placeOfBirth?: string
  gender?: string
  birth?: string
  nameEn?: string
  type?: string
  expiryDate?: string
  placeOfIssue?: string
  issuingAuthority?: string
  nationality?: string
  countryCode?: string
  name?: string
  issueDate?: string
  mrzLine1?: string
  mrzLine2?: string
  direction?: string
  url?: string
  fileId?: string
}

export async function uploadPersonnelOcrIdCardFront(
  file: File,
): Promise<PersonnelOcrIdCardFront | null> {
  const fd = new FormData()
  fd.append('idCardFront', file)
  const raw = await http.upload<{ idCardFront?: PersonnelOcrIdCardFront }>(
    `${PERSONNEL_OCR_BASE}/ocr/personnel/idCardFront`,
    fd,
  )
  return raw?.idCardFront ?? null
}

export async function uploadPersonnelOcrIdCardBack(
  file: File,
): Promise<PersonnelOcrIdCardBack | null> {
  const fd = new FormData()
  fd.append('idCardBack', file)
  const raw = await http.upload<{ idCardBack?: PersonnelOcrIdCardBack }>(
    `${PERSONNEL_OCR_BASE}/ocr/personnel/idCardBack`,
    fd,
  )
  return raw?.idCardBack ?? null
}

export async function uploadPersonnelOcrPassport(file: File): Promise<PersonnelOcrPassport | null> {
  const fd = new FormData()
  fd.append('passport', file)
  const raw = await http.upload<{ passport?: PersonnelOcrPassport }>(
    `${PERSONNEL_OCR_BASE}/ocr/passport`,
    fd,
  )
  return raw?.passport ?? null
}

export {
  isCnMobile as isValidChinaMobile,
  isContactTel as isValidContactPhone,
} from '@/utils/phone'

/** GET /biz/overseas/personnel/page 原始 data（records 与 list 二选一） */
interface OverseasPersonnelPageData {
  records?: unknown[]
  list?: unknown[]
  total?: number
  size?: number
  current?: number
}

function pickCoord(o: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const raw = o[key]
    if (raw == null || raw === '') continue
    const n = Number(raw)
    if (Number.isFinite(n)) return n
  }
  return null
}

function pickStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v != null && v !== '') return String(v)
  }
  return ''
}

/** 主键：后端常为 number，与 pickStr 不同 */
function pickId(o: Record<string, unknown>): string {
  const v = o.id
  if (v != null && v !== '') return String(v)
  return ''
}

function normalizeOverseasListStatus(v: unknown): OverseasPersonnelListStatus | '' {
  const s = String(v ?? '').toUpperCase()
  if (!s) return ''
  return s === 'RETURNED' ? 'RETURNED' : 'OVERSEAS'
}

function rawToOverseasListItem(raw: unknown): OverseasPersonnelListItem | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = pickId(o)
  if (!id) return null
  const g = o.gender
  return {
    id,
    idCard: pickStr(o, 'idCard', 'idCardNo'),
    name: pickStr(o, 'name'),
    birthDate: pickStr(o, 'birthDate'),
    passportNo: pickStr(o, 'passportNo'),
    gender: g !== undefined && g !== null ? (g as string | number) : 1,
    passportValidity: normalizePassportValidityToIso(
      pickStr(o, 'passportValidity', 'passportExpiry'),
    ),
    phone: pickStr(o, 'phone', 'contactPhone', 'mobile'),
    ...(() => {
      const rawCode = pickStr(o, 'overseasCountry', 'overseas_country', 'countryRegion').trim()
      const rawName = pickStr(o, 'overseasCountryName', 'overseas_country_name').trim()
      const looksLikeCode = /^[A-Za-z]{2,3}$/.test(rawCode)
      const overseasCountryCode = rawName ? rawCode : looksLikeCode ? rawCode : ''
      const overseasCountryName = rawName || (!looksLikeCode && rawCode ? rawCode : '')
      const overseasCountry = overseasCountryName || overseasCountryCode || rawCode
      return { overseasCountry, overseasCountryCode, overseasCountryName }
    })(),
    domesticAddress: pickStr(o, 'domesticAddress', 'address'),
    overseasAddress: pickStr(o, 'overseasAddress', 'overseas_address'),
    overseasLongitude: pickCoord(o, 'overseasLongitude', 'overseas_longitude'),
    overseasLatitude: pickCoord(o, 'overseasLatitude', 'overseas_latitude'),
    domesticProvince: pickStr(o, 'domesticProvince', 'householdProvince'),
    domesticAddressCode: pickStr(o, 'domesticAddressCode', 'domestic_address_code'),
    emergencyContact: pickStr(o, 'emergencyContact', 'emergencyContactName'),
    emergencyPhone: pickStr(o, 'emergencyPhone', 'emergencyContactPhone'),
    nation: pickStr(o, 'nation'),
    travelPurpose: pickStr(o, 'travelPurpose', 'travel_purpose'),
    personnelCategory: pickStr(o, 'personnelCategory', 'personnel_category'),
    hasReturnPlan: pickStr(o, 'hasReturnPlan', 'has_return_plan'),
    proxyForOthers: pickStr(o, 'proxyForOthers', 'proxy_for_others'),
    expectedReturnDate: pickStr(o, 'expectedReturnDate', 'expected_return_date'),
    remark: pickStr(o, 'remark'),
    status: normalizeOverseasListStatus(o.status),
    issueAuthority: pickStr(o, 'issueAuthority', 'issue_authority'),
    idCardValidity: normalizeIdCardValidityToStandardFormat(
      pickStr(o, 'idCardValidity', 'id_card_validity'),
    ),
    idCardAddress: pickStr(o, 'idCardAddress', 'id_card_address'),
    visaValidity: pickStr(o, 'visaValidity', 'visa_validity'),
    enterpriseId: pickStr(o, 'enterpriseId', 'enterprise_id'),
    enterpriseName: pickStr(o, 'enterpriseName', 'enterprise_name'),
    overseasPhone: pickStr(o, 'overseasPhone', 'overseas_phone'),
  }
}

export async function fetchForeignPersonnelPage(
  params: ForeignPersonnelPageParams,
): Promise<ForeignPersonnelPageResult> {
  const query: Record<string, string | number | undefined> = {
    current: params.current,
    size: params.size,
    searchKey: params.searchKey?.trim() || undefined,
    areaCode: params.areaCode?.trim() || undefined,
    status: params.status?.trim() || undefined,
    enterpriseId: params.enterpriseId?.trim() || undefined,
    overseasCountry: params.overseasCountry?.trim() || undefined,
  }
  const raw = await http.get<OverseasPersonnelPageData>('/biz/overseas/personnel/page', {
    params: query,
  })
  const records = Array.isArray(raw.records) ? raw.records : Array.isArray(raw.list) ? raw.list : []
  const list = records
    .map(rawToOverseasListItem)
    .filter((x): x is OverseasPersonnelListItem => x != null)
  const parsed = Number(raw.total)
  const total = Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : list.length
  return { list, total }
}

export interface PersonnelDropdownItem {
  id: string
  name: string
  idCard?: string
  phone?: string
  enterpriseId?: string
}

/**
 * GET /biz/overseas/personnel/dropdownList
 * 用于归国登记等弹窗选择人员（不走 /page 权限）
 * 敏感字段不解密，返回库内密文；前端仅用姓名选人
 */
export async function fetchPersonnelDropdownList(
  searchKey?: string,
): Promise<PersonnelDropdownItem[]> {
  const raw = await http.get<unknown>('/biz/overseas/personnel/dropdownList', {
    params: { searchKey: searchKey?.trim() || undefined },
  })
  if (!Array.isArray(raw)) return []
  return raw
    .map((x) => {
      const o = x as Record<string, unknown>
      const id = o.id != null ? String(o.id).trim() : ''
      const name = o.name != null ? String(o.name).trim() : ''
      if (!id || !name) return null
      return {
        id,
        name,
        idCard: o.idCard != null ? String(o.idCard).trim() : undefined,
        phone: o.phone != null ? String(o.phone).trim() : undefined,
        enterpriseId: o.enterpriseId != null ? String(o.enterpriseId).trim() : undefined,
      } as PersonnelDropdownItem
    })
    .filter((x): x is PersonnelDropdownItem => x != null)
}

/** GET /biz/overseas/personnel/detail 人员详情（敏感字段为库内密文原值） */
export async function fetchForeignPersonnelDetail(
  id: string,
  options?: { skipErrorMessage?: boolean },
): Promise<OverseasPersonnelDetail | null> {
  const sid = id?.trim()
  if (!sid) return null
  return await http.get<OverseasPersonnelDetail>('/biz/overseas/personnel/detail', {
    params: { id: sid },
    skipErrorMessage: options?.skipErrorMessage,
  })
}

/**
 * GET /biz/overseas/personnel/detailPlain
 * 敏感字段明文（编辑回填 / 详情点解密；后端记审计日志）
 */
export async function fetchForeignPersonnelDetailPlain(
  id: string,
): Promise<OverseasPersonnelDetail | null> {
  const sid = id?.trim()
  if (!sid) return null
  return await http.get<OverseasPersonnelDetail>('/biz/overseas/personnel/detailPlain', {
    params: { id: sid },
  })
}

/** GET /biz/overseas/personnel/phonePlain — 仅解密手机号明文（案件联系电话回填等） */
export async function fetchForeignPersonnelPhonePlain(
  id: string,
): Promise<{ id: string; phone: string } | null> {
  const sid = id?.trim()
  if (!sid) return null
  const raw = await http.get<{ id?: string; phone?: string } | null>(
    '/biz/overseas/personnel/phonePlain',
    { params: { id: sid } },
  )
  if (raw == null || typeof raw !== 'object') return null
  return {
    id: String(raw.id ?? sid).trim(),
    phone: String(raw.phone ?? '').trim(),
  }
}

export async function createForeignPersonnel(payload: ForeignPersonnelSavePayload): Promise<void> {
  await saveOverseasPersonnel(payload, null)
}

export async function updateForeignPersonnel(
  id: string,
  payload: ForeignPersonnelSavePayload,
): Promise<void> {
  await saveOverseasPersonnel(payload, id)
}

/** POST `/biz/overseas/personnel/delete`，body：`{ id }` */
export async function deleteForeignPersonnel(id: string): Promise<void> {
  await http.post<void>('/biz/overseas/personnel/delete', { id })
}

/**
 * GET /biz/overseas/personnel/self
 * 获取当前登录用户的本人人员档案信息
 */
export async function fetchCurrentSelfPersonnel(): Promise<OverseasPersonnelDetail | null> {
  try {
    const result = await http.get<{ data: OverseasPersonnelDetail }>('/biz/overseas/personnel/self')
    return result?.data ?? null
  } catch {
    return null
  }
}

/** POST /biz/overseas/personnel/import 返回体中的单行错误 */
export interface OverseasPersonnelImportErrorItem {
  row: number
  message: string
}

export interface OverseasPersonnelImportResult {
  totalCount: number
  successCount: number
  errorCount: number
  errorDetail: OverseasPersonnelImportErrorItem[]
  failedExcelBase64?: string
  failedExcelFileName?: string
  failedExcelError?: string
}

function unwrapImportApiData(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  const inner = o.data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return o
}

function importResultNum(v: unknown): number {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function parseOverseasPersonnelImportResult(raw: unknown): OverseasPersonnelImportResult {
  const o = unwrapImportApiData(raw)
  if (!o) {
    return { totalCount: 0, successCount: 0, errorCount: 0, errorDetail: [] }
  }
  const errors: OverseasPersonnelImportErrorItem[] = []
  const errRaw = o.errorDetail
  if (Array.isArray(errRaw)) {
    for (const item of errRaw) {
      if (item != null && typeof item === 'object' && !Array.isArray(item)) {
        const e = item as Record<string, unknown>
        errors.push({
          row: importResultNum(e.row),
          message: pickStr(e, 'message') || pickStr(e, 'msg'),
        })
      }
    }
  }
  return {
    totalCount: importResultNum(o.totalCount),
    successCount: importResultNum(o.successCount),
    errorCount: importResultNum(o.errorCount),
    errorDetail: errors,
    failedExcelBase64: pickStr(o, 'failedExcelBase64') || undefined,
    failedExcelFileName: pickStr(o, 'failedExcelFileName') || undefined,
    failedExcelError: pickStr(o, 'failedExcelError') || undefined,
  }
}

/**
 * GET /biz/overseas/personnel/export — 导出 Excel（筛选条件与列表一致）
 * @param options.decryptSensitive 为 true 时解密四字段后导出；默认 false 加密导出
 */
export async function exportForeignPersonnelData(
  params: ForeignPersonnelPageParams,
  options?: { decryptSensitive?: boolean; decryptBatchSize?: number },
): Promise<void> {
  const query = buildPersonnelExportQuery(params, options)
  await download('/biz/overseas/personnel/export', '黔籍在外人员导出.xlsx', {
    params: query,
    timeout: 120000,
  })
}

function buildPersonnelExportQuery(
  params: ForeignPersonnelPageParams,
  options?: { decryptSensitive?: boolean; decryptBatchSize?: number },
): Record<string, string | number | boolean | undefined> {
  return {
    searchKey: params.searchKey?.trim() || undefined,
    areaCode: params.areaCode?.trim() || undefined,
    status: params.status?.trim() || undefined,
    enterpriseId: params.enterpriseId?.trim() || undefined,
    overseasCountry: params.overseasCountry?.trim() || undefined,
    decryptSensitive: options?.decryptSensitive === true ? true : undefined,
    decryptBatchSize:
      options?.decryptSensitive === true && options.decryptBatchSize != null
        ? options.decryptBatchSize
        : undefined,
  }
}

export async function startPersonnelExportJob(
  params: ForeignPersonnelPageParams,
  options: { decryptSensitive: boolean; decryptBatchSize: number },
): Promise<OverseasExportJobStart> {
  const raw = await http.post<unknown>(
    '/biz/overseas/personnel/export/job',
    {},
    {
      params: buildPersonnelExportQuery(params, options),
      timeout: 120000,
      skipErrorMessage: true,
    },
  )
  const started = asJobStart(raw)
  if (!started.jobId) {
    throw new Error('未能创建导出任务')
  }
  return started
}

export async function fetchPersonnelExportJobStatus(
  jobId: string,
): Promise<OverseasExportJobStatus> {
  const raw = await http.get<unknown>(
    `/biz/overseas/personnel/export/job/${encodeURIComponent(jobId)}`,
    {
      skipErrorMessage: true,
      timeout: 20000,
    },
  )
  return asJobStatus(raw)
}

export async function downloadPersonnelExportJobFile(jobId: string): Promise<void> {
  await downloadExportJobExcel(
    `/biz/overseas/personnel/export/job/${encodeURIComponent(jobId)}/file`,
    '黔籍在外人员导出.xlsx',
  )
}

/**
 * GET /biz/overseas/personnel/importTemplate — 下载导入模板（Excel）
 */
export async function downloadOverseasPersonnelImportTemplate(): Promise<void> {
  await download('/biz/overseas/personnel/importTemplate', '黔籍在外人员批量导入模板.xlsx')
}

/** 获取导入模板二进制（用于导出通过/失败明细，与「下载模板」同源） */
export async function fetchOverseasPersonnelImportTemplate(): Promise<ArrayBuffer> {
  const blob = await http.getBlob('/biz/overseas/personnel/importTemplate')
  return blob.arrayBuffer()
}

function parseImportPreviewResult(
  raw: unknown,
): import('@/types/importPreview').ImportPreviewResult {
  const o = unwrapImportApiData(raw)
  if (!o) {
    return { totalCount: 0, importableCount: 0, errorCount: 0, rows: [] }
  }
  const rowsRaw = o.rows
  const rows: import('@/types/importPreview').ImportPreviewRow[] = []
  if (Array.isArray(rowsRaw)) {
    for (const item of rowsRaw) {
      if (!item || typeof item !== 'object') continue
      const r = item as Record<string, unknown>
      const dbFieldsRaw = r.dbFields
      const dbFields: import('@/types/importPreview').ImportDbFieldItem[] = []
      if (Array.isArray(dbFieldsRaw)) {
        for (const f of dbFieldsRaw) {
          if (!f || typeof f !== 'object') continue
          const x = f as Record<string, unknown>
          dbFields.push({
            column: pickStr(x, 'column'),
            label: pickStr(x, 'label'),
            value: pickStr(x, 'value'),
            confidential: x.confidential === true,
            inferred: x.inferred === true,
            autoGenerated: x.autoGenerated === true,
          })
        }
      }
      rows.push({
        excelRow: importResultNum(r.excelRow),
        rowLabel: pickStr(r, 'rowLabel') || '（无名称）',
        dbFields,
        warnings: Array.isArray(r.warnings) ? r.warnings.map(String) : [],
        errors: Array.isArray(r.errors) ? r.errors.map(String) : [],
        importable: r.importable === true || r.importable === 'true' || r.importable === 1,
      })
    }
  }
  return {
    totalCount: importResultNum(o.totalCount),
    importableCount: importResultNum(o.importableCount),
    errorCount: importResultNum(o.errorCount),
    icspEnabled: o.icspEnabled === true,
    confidentialHint: pickStr(o, 'confidentialHint') || undefined,
    rows,
  }
}

/**
 * POST /biz/overseas/personnel/import/preview — 导入入库预览
 */
export async function previewOverseasPersonnelImport(
  file: File,
): Promise<import('@/types/importPreview').ImportPreviewResult> {
  const fd = new FormData()
  fd.append('file', file)
  const raw = await http.upload<unknown>('/biz/overseas/personnel/import/preview', fd, {
    timeout: OVERSEAS_IMPORT_HTTP_TIMEOUT_MS,
  })
  return parseImportPreviewResult(raw)
}

/**
 * POST /biz/overseas/personnel/import/preview/chunk — 分批入库预览
 */
export async function previewOverseasPersonnelChunk(
  rows: import('@/types/importPreview').ImportChunkRowPayload[],
): Promise<import('@/types/importPreview').ImportPreviewResult> {
  const raw = await http.post<unknown>(
    '/biz/overseas/personnel/import/preview/chunk',
    { rows },
    { timeout: OVERSEAS_IMPORT_PREVIEW_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
  return parsePreviewChunkResult(raw)
}

/**
 * POST /biz/overseas/personnel/import/prepare — 预热查重与字段推断索引
 */
export async function prepareOverseasPersonnelImport(): Promise<void> {
  await http.post<unknown>(
    '/biz/overseas/personnel/import/prepare',
    {},
    { timeout: OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
}

/** POST /biz/overseas/personnel/import/cache/refresh — 删除 Redis 并从数据库重建导入缓存 */
export async function refreshOverseasPersonnelImportCache(): Promise<void> {
  await http.post<unknown>(
    '/biz/overseas/personnel/import/cache/refresh',
    {},
    { timeout: OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
}

/**
 * POST /biz/overseas/personnel/import/chunk — 分批导入
 */
export async function importOverseasPersonnelChunk(
  rows: import('@/types/importPreview').ImportChunkRowPayload[],
): Promise<import('@/types/importPreview').ImportChunkResult> {
  const raw = await http.post<unknown>(
    '/biz/overseas/personnel/import/chunk',
    { rows },
    { timeout: OVERSEAS_IMPORT_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
  return parseImportChunkResult(raw)
}

/**
 * POST /biz/overseas/personnel/import — 批量导入（multipart，字段名 file）
 */
export async function importOverseasPersonnelFile(
  file: File,
): Promise<OverseasPersonnelImportResult> {
  const fd = new FormData()
  fd.append('file', file)
  const raw = await http.upload<unknown>('/biz/overseas/personnel/import', fd)
  return parseOverseasPersonnelImportResult(raw)
}
